import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Footer from 'Components/ClientApp/Desktop/Footer';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { noop } from 'Helpers/PropTypes';
import {
  FDBackButton, FDBodyText, FDContentContainer, FDHeadlineText,
} from '@facet-wealth/design';

const useStyles = buildUseStyles('ProfileSubpage', (theme) => ({
  root: {
    width: '100%',
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  backNavigator: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`,
    borderBottom: `1px solid ${theme.palette.facet.mediumGray3}`,
    padding: theme.spacing(2, 0),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subtitle: {
    lineHeight: `${theme.spacing(3.5)}px`,
  },

  // Mobile Classes
  mobile: {
    marginBottom: theme.spacing(8),
  },
  footer: {
    position: 'sticky',
    transform: 'initial',
    bottom: 'unset',
  },
}));

const ProfileSubpage = ({
  className,
  backLabel,
  backLink,
  backOnClick,
  title,
  description,
  children,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const { loadPageContentLink } = usePageContentContext();

  const onPrevious = async () => {
    if (backLink) {
      loadPageContentLink(backLink);
    }
    await backOnClick();
  };

  return (
    <>
      <FDContentContainer maxWidth={'xl'}>
        <Box
          className={classNames(classes.root, className, { [classes.mobile]: isMobile })}
        >
          <Box className={classes.content}>
            <Box className={classNames(classes.backNavigator)}>
              <FDBackButton onClick={onPrevious} test={'profile-page-back'} />
              <Typography color={'primary'} variant={'h3'}>
                {backLabel}
              </Typography>
            </Box>
            <FDHeadlineText color="textSecondary" value={title} />
            <FDBodyText value={description} />
            {children}
          </Box>
        </Box>
      </FDContentContainer>
      {!isMobile && <Footer className={classes.footer} />}
    </>
  );
};

ProfileSubpage.defaultProps = {
  className: '',
  backLabel: 'Return to Profile',
  backLink: null,
  backOnClick: noop,
  title: '',
  description: '',
};

ProfileSubpage.propTypes = {
  className: PropTypes.string,
  backLabel: PropTypes.string,
  backLink: PropTypes.instanceOf(fr.Link),
  backOnClick: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default ProfileSubpage;
